import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';

/**
 * Minimal shape required by the version pager — only the fields it actually reads.
 */
export interface VersionPagerItem {
  /**
   * Unique identifier of this version, used for selection and navigation.
   */
  readonly id: number;

  /**
   * Date shown alongside the version position label.
   */
  readonly versionDisplayDate: Date;

  /**
   * Display name of the user who created or amended this version, or null when unavailable.
   */
  readonly versionActorName: string | null;

  /**
   * Whether this version was originally created or produced by an amendment.
   */
  readonly versionActorAction: 'created' | 'amended';
}

@Component({
  selector: 'fb-version-pager',
  imports: [DatePipe],
  templateUrl: './version-pager.component.html',
  styleUrl: './version-pager.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VersionPagerComponent {
  /**
   * Ordered version history, oldest first.
   */
  public readonly versions = input.required<readonly VersionPagerItem[]>();

  /**
   * ID of the version currently on screen, or null when none is selected yet.
   */
  public readonly selection = input<number | null>(null);

  /**
   * Disables both arrows, e.g. while the host is still loading the selected version.
   */
  public readonly disabled = input(false);

  /**
   * Emits the ID of the version the user navigated to.
   */
  public readonly selectionChange = output<number>();

  /**
   * Index of the selected version in the versions array, -1 when not found.
   */
  protected readonly currentIndex = computed(() =>
    this.versions().findIndex((v) => v.id === this.selection()),
  );

  /**
   * Whether the pager has a valid selection to display.
   */
  public readonly visible = computed(() => this.currentIndex() >= 0);

  /**
   * 1-based position of the selected version.
   */
  public readonly position = computed(() => this.currentIndex() + 1);

  /**
   * Total number of versions.
   */
  public readonly total = computed(() => this.versions().length);

  /**
   * Display date of the selected version.
   */
  public readonly date = computed(
    () => this.versions()[this.currentIndex()]?.versionDisplayDate ?? null,
  );

  /**
   * Label describing the actor who created/amended this version, or null.
   */
  public readonly actorLabel = computed(() => {
    const version = this.versions()[this.currentIndex()];

    if (!version?.versionActorName) {
      return null;
    }

    const verb = version.versionActorAction === 'amended' ? 'Amended by' : 'Created by';

    return `${verb} ${version.versionActorName}`;
  });

  /**
   * ID of the previous (older) version, or null when already at the oldest.
   */
  public readonly previousId = computed(() => {
    const i = this.currentIndex();

    return i > 0 ? this.versions()[i - 1].id : null;
  });

  /**
   * ID of the next (newer) version, or null when already at the newest.
   */
  public readonly nextId = computed(() => {
    const i = this.currentIndex();

    return i < this.versions().length - 1 ? this.versions()[i + 1].id : null;
  });

  public navigate(id: number | null): void {
    if (this.disabled() || id === null) {
      return;
    }

    this.selectionChange.emit(id);
  }
}
